import { useQuery } from '@tanstack/react-query';
import type { CheckImageUrlDto } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

/**
 * CAP-009 — fetches the short-lived presigned GET URL for a contribution's
 * captured check image from
 * `GET /api/finance/captures/checks/{contributionId}/image` (Finance's first
 * blob read). Called on demand only — the consuming component mounts inside the
 * ledger row's expansion, so the query is `enabled` only when the row is open;
 * the URL is never fetched for collapsed rows or contributions without an image.
 *
 * The URL expires, so the result is not cached long-term (`staleTime: 0`); a
 * re-expand after expiry refetches. Multi-tenancy rides the standard
 * `authenticatedFetch` headers; the key is cid()-scoped.
 */
export function useContributionCheckImage(
  contributionId: string,
  enabled: boolean,
) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.checkImage(contributionId),
    queryFn: () =>
      apiJson<CheckImageUrlDto>(`/api/finance/captures/checks/${contributionId}/image`, {
        error: 'Failed to load the check image.',
      }),
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
    staleTime: 0,
  });
}
